// Tire-tread mud text — "URBAN OFF-ROAD" and other headline callouts.
// Two parallel tire tracks skid diagonally BEHIND the letters at ~-9°.
// Tread pattern is a chevron zigzag (real off-road tire look).
// Letters have a hard dark outline (stroke) so they stay legible on top of the mud.

const INNER_TREAD = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='40' viewBox='0 0 48 40'>` +
    `<rect width='48' height='40' fill='%23b8834a'/>` +
    // deep zigzag grooves (two rows of chevrons)
    `<g stroke='%23161009' stroke-width='4' fill='none' stroke-linejoin='miter'>` +
    `<polyline points='-4,4 12,16 28,4 44,16 60,4'/>` +
    `<polyline points='-4,24 12,36 28,24 44,36 60,24'/>` +
    `</g>` +
    // light highlights on the ridges
    `<g stroke='%23dcb280' stroke-width='1.5' fill='none' opacity='0.65'>` +
    `<polyline points='-4,2 12,14 28,2 44,14 60,2'/>` +
    `<polyline points='-4,22 12,34 28,22 44,34 60,22'/>` +
    `</g>` +
    `</svg>`;

const MUD_SHADOW = [
    // caked-mud crust hugging every letter (short offsets, dark)
    "1px 1px 0 #2b1f16",
    "2px 2px 0 #2b1f16",
    "3px 3px 0 #2b1f16",
    // dirt streaks kicking up to the lower-right
    "6px 8px 3px rgba(112, 78, 45, 0.85)",
    "10px 14px 5px rgba(150, 110, 70, 0.7)",
    "14px 20px 10px rgba(180, 140, 95, 0.55)",
    // soft dust haze
    "18px 26px 20px rgba(200, 165, 120, 0.35)",
    "0 0 30px rgba(120, 85, 55, 0.35)",
    // upper-left dry-mud speck
    "-2px -1px 0 rgba(58, 44, 34, 0.6)",
].join(", ");

// Generate a zigzag polyline point list along the length of a track.
// centerY is the vertical middle line, amp is the peak/valley distance.
function zigzag(step, count, centerY, amp) {
    return Array.from({ length: count + 1 }, (_, i) => {
        const y = i % 2 === 0 ? centerY - amp : centerY + amp;
        return `${i * step - step},${y}`;
    }).join(" ");
}

// One tire track — a knobby muddy band drawn horizontally with zigzag chevron tread.
function TireTrack({ y }) {
    // Two rows of chevrons across a 1200-wide band
    const upper = zigzag(24, 52, 16, 10);   // peaks 6 / valleys 26
    const lower = zigzag(24, 52, 54, 10);   // peaks 44 / valleys 64
    const upperShine = zigzag(24, 52, 14, 10);
    const lowerShine = zigzag(24, 52, 52, 10);
    return (
        <g transform={`translate(0, ${y})`}>
            {/* muddy base band */}
            <rect x="0" y="-2" width="1200" height="76" fill="#5b3f28" />
            {/* dark rim grooves top + bottom */}
            <rect x="0" y="-6" width="1200" height="6" fill="#1a0f07" />
            <rect x="0" y="72" width="1200" height="6" fill="#1a0f07" />
            {/* central seam */}
            <rect x="0" y="34" width="1200" height="3" fill="#1a0f07" opacity="0.8" />
            {/* dark chevron zigzag treads */}
            <g stroke="#1a0f07" strokeWidth="8" fill="none" strokeLinejoin="miter" strokeLinecap="butt">
                <polyline points={upper} />
                <polyline points={lower} />
            </g>
            {/* light mud shine on top of each chevron */}
            <g stroke="#c8955f" strokeWidth="2.5" fill="none" opacity="0.6">
                <polyline points={upperShine} />
                <polyline points={lowerShine} />
            </g>
        </g>
    );
}

// Two parallel tracks skidding across the letters at -9 degrees.
function TireTracksBg() {
    return (
        <svg
            aria-hidden="true"
            className="pointer-events-none absolute left-[-6%] top-[-22%] h-[150%] w-[112%] opacity-95 mix-blend-multiply"
            viewBox="-50 -30 1200 340"
            preserveAspectRatio="none"
        >
            <g transform="rotate(-9, 550, 145)">
                <TireTrack y={20} />
                <TireTrack y={170} />
            </g>
            {/* soft-fade the ends of the tracks */}
            <defs>
                <linearGradient id="track-fade" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#f5efe0" stopOpacity="1" />
                    <stop offset="0.07" stopColor="#f5efe0" stopOpacity="0" />
                    <stop offset="0.93" stopColor="#f5efe0" stopOpacity="0" />
                    <stop offset="1" stopColor="#f5efe0" stopOpacity="1" />
                </linearGradient>
            </defs>
            <rect x="-50" y="-30" width="1200" height="340" fill="url(#track-fade)" />
        </svg>
    );
}

export function BrushCallout({ children, className = "" }) {
    return (
        <span className={`relative inline-block whitespace-nowrap ${className}`}>
            <TireTracksBg />
            <span
                className="relative inline-block"
                style={{
                    fontFamily: '"Montserrat", system-ui, sans-serif',
                    fontWeight: 900,
                    fontStyle: "normal",
                    textTransform: "uppercase",
                    letterSpacing: "-0.01em",
                    color: "transparent",
                    WebkitTextFillColor: "transparent",
                    // hard dark stroke around each letter so it reads against the tracks
                    WebkitTextStroke: "2.5px #120a04",
                    paintOrder: "stroke fill",
                    backgroundImage: `url("${INNER_TREAD}")`,
                    backgroundSize: "0.45em 0.38em",
                    backgroundRepeat: "repeat",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    textShadow: MUD_SHADOW,
                }}
            >
                {children}
            </span>
        </span>
    );
}

export function ArrowIndicator({ className = "" }) {
    return (
        <svg viewBox="0 0 40 16" className={`inline-block h-3.5 w-9 ${className}`} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 8 C 12 7, 22 9, 35 8" />
            <path d="M28 2 L 36 8 L 28 14" />
        </svg>
    );
}
